<script setup lang="ts">
import { computed, ref } from 'vue';
import { RouterLink } from 'vue-router';
import { useHead } from '@unhead/vue';
import { useSession } from '../auth';
import AppHeader from '@/components/layout/AppHeader.vue';
import AppFooter from '@/components/layout/AppFooter.vue';
import HeroDemo from '@/components/marketing/HeroDemo.vue';
import EditDemo from '@/components/marketing/EditDemo.vue';
import DeleteDemo from '@/components/marketing/DeleteDemo.vue';
import RestoreDemo from '@/components/marketing/RestoreDemo.vue';
import SpacedRepetitionDemo from '@/components/marketing/SpacedRepetitionDemo.vue';
import claudeIcon from '@/assets/logos/claude.svg';
import claudeCodeIcon from '@/assets/logos/claude-code.png';
import chatgptIcon from '@/assets/logos/chatgpt.svg';

useHead({
  title: 'LLM-SR - Flashcards Managed by Your LLM',
  meta: [
    {
      name: 'description',
      content: 'Create and review flashcards with AI assistance. LLM-SR uses the FSRS algorithm for optimal spaced repetition scheduling—less time reviewing, better retention.',
    },
    {
      property: 'og:title',
      content: 'LLM-SR - Flashcards Managed by Your LLM',
    },
    {
      property: 'og:description',
      content: 'Create and review flashcards with AI assistance. LLM-SR uses the FSRS algorithm for optimal spaced repetition scheduling.',
    },
  ],
});

const session = useSession();
const isSignedIn = computed(() => !!session.value?.data?.user);

const llmOptions = [
  { id: 'chatgpt', name: 'ChatGPT', icon: chatgptIcon },
  { id: 'claude', name: 'Claude', icon: claudeIcon },
  { id: 'claude-code', name: 'Claude Code', icon: claudeCodeIcon },
] as const;

type LlmStyle = typeof llmOptions[number]['id'];
const selectedLlm = ref<LlmStyle>('chatgpt');

const demoTabs = [
  { id: 'create', label: 'Create' },
  { id: 'edit', label: 'Edit' },
  { id: 'delete', label: 'Delete' },
  { id: 'restore', label: 'Restore' },
];

const activeTab = ref('create');
const demoKey = ref(0);

function handleTabClick(tabId: string) {
  if (activeTab.value === tabId) {
    // Restart the demo by changing the key
    demoKey.value++;
  } else {
    activeTab.value = tabId;
  }
}
</script>

<template>
  <div class="min-h-screen bg-background">
    <AppHeader />

    <div class="max-w-5xl mx-auto px-4 py-12">
      <!-- Hero -->
      <div class="text-center mb-10">
        <h1 class="text-3xl md:text-4xl font-bold text-foreground mb-4">
          Flashcards, managed by your LLM
        </h1>
        <p class="text-lg text-muted-foreground max-w-2xl mx-auto">
          Learn something new? Turn it into flashcards with a simple request.
        </p>
      </div>

      <!-- LLM Selector -->
      <div class="flex justify-center items-center gap-6 mb-6">
        <span class="text-sm text-muted-foreground">Works with:</span>
        <div class="flex items-center gap-2">
          <button
            v-for="llm in llmOptions"
            :key="llm.id"
            class="flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all cursor-pointer"
            :class="
              selectedLlm === llm.id
                ? 'bg-primary/10 border-primary text-foreground'
                : 'bg-muted/50 border-border text-muted-foreground hover:bg-muted hover:text-foreground'
            "
            @click="selectedLlm = llm.id"
          >
            <span
              v-if="llm.id === 'claude-code'"
              class="w-5 h-5 rounded-md bg-[#1a1a1a] flex items-center justify-center"
            >
              <img :src="llm.icon" :alt="llm.name" class="w-4 h-4" />
            </span>
            <img v-else :src="llm.icon" :alt="llm.name" class="w-5 h-5 rounded-sm" />
            <span class="text-sm">{{ llm.name }}</span>
          </button>
        </div>
      </div>

      <!-- Demo Area -->
      <div class="mb-6">
        <Transition name="fade" mode="out-in">
          <div v-if="activeTab === 'create'" :key="'create-' + demoKey + '-' + selectedLlm">
            <HeroDemo :llm-style="selectedLlm" />
          </div>
          <div v-else-if="activeTab === 'edit'" :key="'edit-' + demoKey + '-' + selectedLlm">
            <EditDemo :llm-style="selectedLlm" />
          </div>
          <div v-else-if="activeTab === 'delete'" :key="'delete-' + demoKey + '-' + selectedLlm">
            <DeleteDemo :llm-style="selectedLlm" />
          </div>
          <div v-else-if="activeTab === 'restore'" :key="'restore-' + demoKey + '-' + selectedLlm">
            <RestoreDemo :llm-style="selectedLlm" />
          </div>
          <div
            v-else
            :key="activeTab"
            class="grid md:grid-cols-2 gap-6"
          >
            <!-- Placeholder left panel -->
            <div>
              <h2 class="text-xl font-semibold text-foreground mb-3">Your favorite LLM</h2>
              <div class="rounded-lg border border-border bg-card overflow-hidden shadow-md">
                <div class="px-3 py-2 border-b border-border bg-muted/50 flex items-center gap-2">
                  <div class="flex gap-1.5">
                    <div class="w-2.5 h-2.5 rounded-full bg-red-400/70"></div>
                    <div class="w-2.5 h-2.5 rounded-full bg-yellow-400/70"></div>
                    <div class="w-2.5 h-2.5 rounded-full bg-green-400/70"></div>
                  </div>
                </div>
                <div class="p-4 h-[380px] flex items-center justify-center">
                  <p class="text-sm text-muted-foreground">Demo coming soon</p>
                </div>
              </div>
            </div>

            <!-- Placeholder right panel -->
            <div>
              <h2 class="text-xl font-semibold text-foreground mb-3">Your flashcards in LLM-SR</h2>
              <div class="rounded-lg border border-border bg-card overflow-hidden shadow-md">
                <div class="px-3 py-2 border-b border-border bg-muted/50 flex items-center gap-2">
                  <div class="flex gap-1.5">
                    <div class="w-2.5 h-2.5 rounded-full bg-red-400/70"></div>
                    <div class="w-2.5 h-2.5 rounded-full bg-yellow-400/70"></div>
                    <div class="w-2.5 h-2.5 rounded-full bg-green-400/70"></div>
                  </div>
                </div>
                <div class="p-4 h-[380px] flex items-center justify-center">
                  <p class="text-sm text-muted-foreground">Demo coming soon</p>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>

      <!-- Demo Tabs -->
      <div class="flex justify-center gap-2 mb-12">
        <button
          v-for="tab in demoTabs"
          :key="tab.id"
          class="px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer"
          :class="
            activeTab === tab.id
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
          "
          @click="handleTabClick(tab.id)"
        >
          {{ tab.label }}
        </button>
      </div>
    </div>

    <!-- Spaced Repetition Section -->
    <div class="bg-muted/30 py-16 mt-16">
      <div class="max-w-5xl mx-auto px-4">
        <div class="text-center mb-12">
          <h2 class="text-2xl md:text-3xl font-bold text-foreground mb-4">
            Test yourself using cutting edge spaced repetition algorithms
          </h2>
          <p class="text-lg text-muted-foreground max-w-2xl mx-auto">
            Review flashcards just when you need to—minimal time, maximum retention.
          </p>
        </div>
        <SpacedRepetitionDemo />

        <!-- Bottom CTA -->
        <div class="flex justify-center gap-4 mt-12">
          <template v-if="isSignedIn">
            <RouterLink
              to="/decks"
              class="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition font-medium"
            >
              Go to Decks
            </RouterLink>
          </template>
          <template v-else>
            <RouterLink
              to="/sign-up"
              class="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition font-medium"
            >
              Get Started — it's free
            </RouterLink>
          </template>
        </div>
      </div>
    </div>

    <AppFooter />
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
